import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Input, Label } from 'reactstrap';
import Breadcrumb from '../../components/common/breadcrumb';
import moment from 'moment';
import { createHandover, getCustodyFileUrl, getCustodyVehicle, getHandoverContext, getMasterData, refuseDelivery, startHandover } from './api';
import { CHECK_COLOR } from './AkisTanimlari';
import SignaturePad from './SignaturePad';
import { downloadTutanakPdf } from './TutanakPdf';
import './devir-teslim.css';

// Handover inspection on the handheld (RUA DQ_CLIE10_0069): the receiving party checks the vehicle,
// records damages (zone + component + damage code + photos), observations/reservations, and both parties sign.
// Places and companies come from the flow definitions (no free text): the server suggests the handover point from
// the vehicle's place in the chain and its orders, fills in locations and parties, and applies the point's checks.

const TRANSPORT_BY_PARTY_TYPE = { KARAYOLU: 'TIR', DEMIRYOLU: 'TREN', DENIZYOLU: 'GEMI' };
const FLOW_NAME = { IHRACAT: 'İhracat', ITHALAT: 'İthalat', YERLI: 'Yerli' };
const SIDE_NAME = { KAYNAK: 'kaynakta teslim', HEDEF: 'hedefte teslim alma', DOGRUDAN: 'doğrudan teslim' };
const emptyHead = { point_code: '', from_location_id: 0, to_location_id: 0, delivering_party_id: 0, receiving_party_id: 0,
    location_name: '', delivering_party: '', delivering_person: '', receiving_party: '', receiving_person: '', transport_mode: 'TIR',
    transport_ref: '', document_ref: '' };
const emptyDamage = { zone_code: '', component_code: '', damage_code: '', size_text: '', note: '', photos: [] };

const currentUser = () => {
    try {
        const t = jwtDecode(Cookies.get('token'));
        return t.full_name || '';
    } catch (e) {
        return '';
    }
};

const StoredSignature = ({ path }) => {
    const [src, setSrc] = useState('');
    useEffect(() => { getCustodyFileUrl(path).then(setSrc).catch(() => setSrc('')); }, [path]);
    return src ? <img src={src} alt="imza" className="dt-stored-sig" /> : null;
};

const activeOnly = (md) => {
    const on = (c) => c.is_active !== false;
    return { ...md, components: md.components.filter(on), damage_codes: md.damage_codes.filter(on), points: md.points.filter(on),
        parties: md.parties.filter(on), locations: md.locations.filter(on) };
};

const DevirTeslim = () => {
    const [md, setMd] = useState(null);
    const [step, setStep] = useState(1);
    const [vin, setVin] = useState('');
    const [vehicle, setVehicle] = useState(null);
    const [undefinedVin, setUndefinedVin] = useState('');
    const [head, setHead] = useState(emptyHead);
    const [ctx, setCtx] = useState(null);             // handover context from the server (flow, orders, checks, defaults)
    const [acceptWarn, setAcceptWarn] = useState(false);
    const [podFiles, setPodFiles] = useState([]); // photo / PDF of the signed delivery note (final delivery)
    // delivery not accepted at the dealer: reason, dealer person, photos; the car goes back to the compound
    const [refusal, setRefusal] = useState({ reason_code: '', note: '', receiver_person: '', photos: [] });
    const [refusalDone, setRefusalDone] = useState(null);
    const [lines, setLines] = useState([]);
    const [draft, setDraft] = useState(null);         // damage being entered
    const [obsOpen, setObsOpen] = useState(false);
    const [componentFilter, setComponentFilter] = useState('');
    const [sig, setSig] = useState({ delivering: '', receiving: '' });
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(null);
    const [startRec, setStartRec] = useState(null);   // open two-step handover being completed
    const [startNote, setStartNote] = useState('');
    const [startDone, setStartDone] = useState(null);
    const userName = useMemo(currentUser, []);

    useEffect(() => { getMasterData().then((d) => setMd(activeOnly(d))).catch(() => toast.error('Kod listeleri alınamadı')); }, []);

    const byCode = (list) => Object.fromEntries((list || []).map((c) => [c.code, c]));
    const comps = useMemo(() => byCode(md?.components), [md]);
    const dmg = useMemo(() => byCode(md?.damage_codes), [md]);
    const zones = useMemo(() => byCode(md?.zones), [md]);
    const obs = useMemo(() => byCode(md?.observations), [md]);
    const point = (md?.points || []).find((p) => p.code === head.point_code);

    const partyName = (id) => md?.parties.find((p) => p.id === id)?.name || '';
    const locById = (id) => md?.locations.find((l) => l.id === id);

    // applies the server's defaults; derived names are only for display (the server resolves them from the ids)
    const applyContext = (c, keepPeople) => {
        setCtx(c);
        setAcceptWarn(false);
        const p = md.points.find((x) => x.code === c.point_code);
        setHead((h) => {
            const n = { ...h, point_code: c.point_code || '', from_location_id: c.from_location_id || 0, to_location_id: c.to_location_id || 0,
                delivering_party_id: c.delivering_party_id || (keepPeople ? h.delivering_party_id : 0),
                receiving_party_id: c.receiving_party_id || (keepPeople ? h.receiving_party_id : 0) };
            if (!keepPeople) { n.delivering_person = ''; n.receiving_person = ''; }
            // a car on a groupage truck: the plate, groupage and driver come from the truck assignment
            if (c.transport_ref && !n.transport_ref) n.transport_ref = c.transport_ref;
            if (c.document_ref && !n.document_ref) n.document_ref = c.document_ref;
            if (c.delivering_person && !n.delivering_person) n.delivering_person = c.delivering_person;
            if (p && (p.from_location_type === 'GEMI' || p.to_location_type === 'GEMI') && vehicle?.vessel_name && !n.transport_ref) n.transport_ref = vehicle.vessel_name;
            return n;
        });
    };

    const refreshContext = async (over, keepPeople = true) => {
        try {
            const c = await getHandoverContext({ chassis_no: vehicle.chassis_no, point_code: head.point_code,
                from_location_id: head.from_location_id, to_location_id: head.to_location_id, ...over });
            applyContext(c, keepPeople);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Bilgiler alınamadı');
        }
    };

    const loadVehicle = async () => {
        const v = vin.trim().toUpperCase();
        if (v.length < 6) return;
        setUndefinedVin('');
        try {
            const data = await getCustodyVehicle(v);
            setVehicle(data);
            const c = await getHandoverContext({ chassis_no: data.chassis_no });
            setHead({ ...emptyHead, transport_ref: '' });
            applyContext(c, false);
        } catch (e) {
            setVehicle(null);
            setCtx(null);
            const msg = e?.response?.data?.error || 'Araç bulunamadı';
            // a VIN not defined yet can still be recorded: it waits and is linked when the car is defined
            if (v.length === 17 && /bulunamad/.test(msg)) setUndefinedVin(v);
            else toast.error(msg);
        }
    };
    const continueUndefined = async () => {
        try {
            const c = await getHandoverContext({ chassis_no: undefinedVin });
            setVehicle({ chassis_no: undefinedVin, brand: '', model: '', colour: '', colour_code: '', open_damages: [], handover_count: 0, undefined: true });
            setHead({ ...emptyHead, transport_ref: '' });
            applyContext(c, false);
            setUndefinedVin('');
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Kayıt açılamadı');
        }
    };

    const carrierType = (id) => md?.parties.find((p) => p.id === id)?.party_types.find((t) => TRANSPORT_BY_PARTY_TYPE[t]);
    const transportMode = () => {
        if (!point) return head.transport_mode;
        const gemi = point.from_location_type === 'GEMI' || point.to_location_type === 'GEMI';
        if (gemi) return 'GEMI';
        const carrier = point.side === 'KAYNAK' ? head.receiving_party_id : point.side === 'HEDEF' ? head.delivering_party_id : 0;
        return carrier ? (TRANSPORT_BY_PARTY_TYPE[carrierType(carrier)] || 'DIGER') : 'SURUS';
    };
    const derived = { delivering_party: partyName(head.delivering_party_id) || head.delivering_party,
        receiving_party: partyName(head.receiving_party_id) || head.receiving_party,
        location_name: locById(ctx?.at_location_id)?.name || head.location_name, transport_mode: transportMode() };
    const H = startRec ? head : { ...head, ...derived };
    const partyOptions = (types) => (md?.parties || []).filter((p) => !types || types.length === 0 || p.party_types.some((t) => types.includes(t)));
    const locOptions = (type) => {
        let list = (md?.locations || []).filter((l) => !type || l.location_type === type);
        if (type === 'GEMI' && vehicle?.vessel_name) {
            list = [...list].sort((a, b) => {
                const aMatch = a.name.trim().toLowerCase() === vehicle.vessel_name.trim().toLowerCase();
                const bMatch = b.name.trim().toLowerCase() === vehicle.vessel_name.trim().toLowerCase();
                if (aMatch && !bMatch) return -1;
                if (!aMatch && bMatch) return 1;
                return a.name.localeCompare(b.name, 'tr');
            });
        }
        return list;
    };
    const failed = (ctx?.checks || []).filter((c) => !c.ok);
    const blocked = failed.some((c) => c.mode === 'ENGEL');
    const warned = failed.some((c) => c.mode === 'UYARI');
    const headReady = vehicle && point && head.delivering_party_id && head.receiving_party_id && head.delivering_person.trim()
        && (!point.from_location_type || head.from_location_id) && (!point.to_location_type || head.to_location_id) && !blocked && (!warned || acceptWarn);

    const setPoint = (code) => refreshContext({ point_code: code, from_location_id: 0, to_location_id: 0 }, false);
    const setLoc = (field, id) => refreshContext({ [field]: id });

    const addDamage = () => {
        const d = draft;
        if (!d.zone_code || !d.component_code || !d.damage_code) return toast.warning('Bölge, parça ve hasar kodu seçiniz');
        if (d.photos.length === 0) return toast.warning('En az bir fotoğraf ekleyiniz');
        if (d.component_code === '45' && !d.note.trim()) return toast.warning('Listede olmayan parça için açıklama yazınız');
        setLines((ls) => [...ls, { line_type: 'HASAR', ...d }]);
        setDraft(null);
        setComponentFilter('');
    };

    const addObservation = (code) => {
        if (lines.some((l) => l.observation_code === code)) return;
        setLines((ls) => [...ls, { line_type: obs[code].line_type, observation_code: code, note: '', photos: [] }]);
        setObsOpen(false);
    };

    // two-step: receiving party completes a handover the delivering party already signed
    const completeStart = () => {
        const st = vehicle.open_start;
        setStartRec(st);
        setHead({ ...emptyHead, point_code: st.point_code, location_name: st.location_name, delivering_party: st.delivering_party,
            delivering_person: st.delivering_person, receiving_party: st.receiving_party, transport_mode: st.transport_mode,
            transport_ref: st.transport_ref || '', document_ref: st.document_ref || '', from_location_id: st.from_location_id || 0,
            to_location_id: st.to_location_id || 0, delivering_party_id: st.delivering_party_id || 0, receiving_party_id: st.receiving_party_id || 0 });
        setStep(2);
    };

    const submitStart = async () => {
        if (!sig.delivering) return toast.warning('Teslim edenin imzası gerekli');
        setSaving(true);
        try {
            const res = await startHandover({ chassis_no: vehicle.chassis_no, ...head, ...derived, note: startNote, delivering_signature: sig.delivering,
                accept_warnings: acceptWarn });
            setStartDone(res);
            setStep(6);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Kayıt başarısız');
        } finally {
            setSaving(false);
        }
    };

    const startRefusal = () => {
        const wrongPlace = (ctx?.checks || []).some((c) => c.check === 'TESLIM_YERI' && !c.ok);
        setSig({ delivering: '', receiving: '' });
        setRefusal({ reason_code: wrongPlace ? 'YANLIS_BAYI' : '', note: '', receiver_person: head.receiving_person || '', photos: [] });
        setStep(7);
    };

    const submitRefusal = async () => {
        setSaving(true);
        try {
            const res = await refuseDelivery({ chassis_no: vehicle.chassis_no, location_id: head.to_location_id, reason_code: refusal.reason_code,
                note: refusal.note, driver_person: head.delivering_person, receiver_party_id: head.receiving_party_id,
                receiver_person: refusal.receiver_person, driver_signature: sig.delivering, receiver_signature: sig.receiving, source: 'SURUCU' },
            refusal.photos);
            setRefusalDone(res);
            setStep(8);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Kayıt başarısız');
        } finally {
            setSaving(false);
        }
    };

    const submit = async () => {
        if ((!startRec && !sig.delivering) || !sig.receiving) return toast.warning('İki tarafın imzası gerekli');
        setSaving(true);
        try {
            const res = await createHandover({ chassis_no: vehicle.chassis_no, ...head, ...(startRec ? {} : derived), accept_warnings: acceptWarn,
                delivering_signature: startRec ? '' : sig.delivering, receiving_signature: sig.receiving, start_id: startRec ? startRec.id : 0,
                device_info: `${navigator.userAgent.slice(0, 120)}` }, lines, podFiles);
            setDone(res);
            setStep(4);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Kayıt başarısız');
        } finally {
            setSaving(false);
        }
    };

    const restart = () => {
        setStep(1); setVin(''); setVehicle(null); setLines([]); setDraft(null); setSig({ delivering: '', receiving: '' }); setDone(null);
        setStartRec(null); setStartNote(''); setStartDone(null); setCtx(null); setHead(emptyHead); setAcceptWarn(false); setPodFiles([]);
        setRefusal({ reason_code: '', note: '', receiver_person: '', photos: [] }); setRefusalDone(null);
    };

    const damageCount = lines.filter((l) => l.line_type === 'HASAR').length;
    const filteredComps = (md?.components || []).filter((c) => {
        const f = componentFilter.trim().toLocaleLowerCase('tr');
        return !f || c.code.startsWith(f) || c.name_tr.toLocaleLowerCase('tr').includes(f) || c.name_en.toLowerCase().includes(f);
    });

    if (!md) return <div className="p-3">Yükleniyor…</div>;

    return (
        <div className="dt-page">
            <Breadcrumb parent="Terminal" title="Devir-Teslim Kontrolü (RUA)" />
            <div className="dt-steps">
                {['Araç', 'Kontrol', 'İmza'].map((s, i) => (
                    <div key={s} className={`dt-step ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'done' : ''}`}>{i + 1}. {s}</div>
                ))}
            </div>

            {step === 1 && (
                <Card><CardBody>
                    <Label>Şase (VIN)</Label>
                    <div className="d-flex">
                        <Input value={vin} placeholder="Okutunuz veya yazınız" autoFocus
                            onChange={(e) => setVin(e.target.value.toUpperCase())}
                            onKeyDown={(e) => e.key === 'Enter' && loadVehicle()} />
                        <Button color="primary" className="ml-2" onClick={loadVehicle}>Getir</Button>
                    </div>
                    {vehicle && (
                        <div className="dt-vehicle">
                            <div className="dt-vin">{vehicle.chassis_no}</div>
                            <div>{vehicle.brand} {vehicle.model}</div>
                            <div>{vehicle.colour} ({vehicle.colour_code}){vehicle.vessel_name ? ` · Gemi: ${vehicle.vessel_name}` : ''}</div>
                            <div className="mt-1">
                                <Badge color={vehicle.handover_count ? 'info' : 'secondary'}>Önceki devir-teslim: {vehicle.handover_count}</Badge>{' '}
                                <Badge color={vehicle.open_damages.length ? 'danger' : 'success'}>Açık hasar: {vehicle.open_damages.length}</Badge>
                            </div>
                            {vehicle.open_damages.map((d) => (
                                <div key={d.id} className="dt-open">{d.component_code} {d.component_name} · {d.damage_code} {d.damage_name} ({d.handover_no}, {d.status_name})</div>
                            ))}
                            {vehicle.open_start && (
                                <div className="dt-pending">
                                    <b>Bekleyen devir-teslim {vehicle.open_start.start_no}</b><br />
                                    {vehicle.open_start.point_name} · {vehicle.open_start.location_name}<br />
                                    {vehicle.open_start.delivering_party} ({vehicle.open_start.delivering_person}) teslim etti,{' '}
                                    {moment(vehicle.open_start.started_at).format('DD.MM.YYYY HH:mm')}
                                    {vehicle.open_start.note && <div className="dt-muted">{vehicle.open_start.note}</div>}
                                    <Button block color="warning" className="mt-2" onClick={completeStart}>Kontrol edip teslim al</Button>
                                </div>
                            )}
                        </div>
                    )}
                    {undefinedVin && (
                        <div className="dt-draft mt-2">
                            <b>{undefinedVin}</b> sistemde tanımlı değil (üretim / gemi listesi henüz gelmemiş olabilir).
                            <div className="dt-muted">Devir-teslim ve hasar şimdi kaydedilebilir; kayıt bekletilir ve araç tanımlandığında otomatik bağlanır.</div>
                            <Button color="warning" className="mt-2" onClick={continueUndefined}>Tanımsız araç olarak kaydet</Button>
                        </div>
                    )}
                    {vehicle && ctx && !vehicle.open_start && (
                        <>
                            <div className="dt-flow">
                                {ctx.undefined_vehicle && <div><Badge color="warning">Tanımsız araç</Badge> kayıt bekletilecek, araç tanımlanınca bağlanacak</div>}
                                <Badge color={ctx.flow === 'IHRACAT' ? 'primary' : ctx.flow === 'ITHALAT' ? 'info' : 'secondary'}>{FLOW_NAME[ctx.flow]}</Badge>
                                {!ctx.undefined_vehicle && <Badge color={ctx.customs_status === 'milli' ? 'success' : 'warning'}>{ctx.customs_status === 'milli' ? 'Milli (vergisi ödenmiş)' : `Gümrük kısıtlı (${ctx.customs_status || '?'})`}</Badge>}
                                <div className="dt-muted">{ctx.flow_reason}</div>
                                <div>Emir: {ctx.orders.length ? ctx.orders.map((o) => `${o.process_type} ${o.ref}${o.destination ? ` → ${o.destination}` : ''}`).join(' · ') : <b className="text-danger">aktif emir yok</b>}</div>
                                {ctx.driver_mode && <div><span className="badge badge-info">Şoför</span> taşıyıcı: kullanıcının firması</div>}
                                {!ctx.in_transit_with && ctx.recorded_location_name && ctx.recorded_location_name !== ctx.current_location_name && (
                                    <div className="text-warning">Sistemde: {ctx.recorded_location_name} (terminal sahası esas alındı)</div>
                                )}
                                <div>Konum: {ctx.in_transit_with ? `yolda (${ctx.in_transit_with})` : (ctx.current_location_name || 'bilinmiyor')}
                                    {ctx.last_handover_no && <span className="dt-muted"> · son devir-teslim {ctx.last_handover_no}</span>}</div>
                                {ctx.return_case && (
                                    <div><Badge color="warning">İade {ctx.return_case.case_no}</Badge>{' '}
                                        {{ YOLDA: 'depoya dönüyor', DEPODA: 'depoda, karar bekliyor' }[ctx.return_case.status] || ctx.return_case.status} · {ctx.return_case.reason_name}</div>
                                )}
                                {ctx.not_born && <div className="dt-muted">Araç {ctx.not_born}</div>}
                                {ctx.chain_ended && <div className="text-danger">Zincir tamamlanmış (son nokta zinciri bitiriyor).</div>}
                            </div>
                            <Label className="mt-2">Devir-teslim noktası</Label>
                            <Input type="select" value={head.point_code} onChange={(e) => setPoint(e.target.value)}>
                                {!head.point_code && <option value="">seçiniz…</option>}
                                {md.points.map((p) => <option key={p.code} value={p.code}>{p.code === ctx.suggested_point ? '★ ' : ''}{p.name_tr}</option>)}
                            </Input>
                            {point && <div className="dt-muted">{SIDE_NAME[point.side]}{point.ends_chain ? ' · zinciri bitirir' : ''}{ctx.active_order ? ` · emir ${ctx.active_order.process_type} ${ctx.active_order.ref}` : ''}</div>}
                            {point && (
                                // one per row: location names are long and the handheld screen is narrow
                                <div className="mt-2">
                                    <div><Label>Nereden</Label>
                                        <Input type="select" value={head.from_location_id || ''} onChange={(e) => setLoc('from_location_id', Number(e.target.value))}>
                                            <option value="">seçiniz…</option>
                                            {locOptions(point.from_location_type).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                        </Input></div>
                                    <div className="mt-2"><Label>Nereye</Label>
                                        <Input type="select" value={head.to_location_id || ''} onChange={(e) => setLoc('to_location_id', Number(e.target.value))}>
                                            <option value="">seçiniz…</option>
                                            {locOptions(point.to_location_type).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                        </Input></div>
                                </div>
                            )}
                            {point && (
                                <>
                                    <div className="d-flex mt-2">
                                        <div className="flex-fill mr-2"><Label>Teslim eden firma</Label>
                                            <Input type="select" value={head.delivering_party_id || ''} onChange={(e) => setHead({ ...head, delivering_party_id: Number(e.target.value) })}>
                                                <option value="">seçiniz…</option>
                                                {partyOptions(ctx.delivering_party_types).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </Input></div>
                                        <div className="flex-fill"><Label>Teslim eden kişi</Label>
                                            <Input value={head.delivering_person} placeholder="Ad soyad / unvan" onChange={(e) => setHead({ ...head, delivering_person: e.target.value })} /></div>
                                    </div>
                                    <div className="d-flex mt-2">
                                        <div className="flex-fill mr-2"><Label>Teslim alan firma</Label>
                                            <Input type="select" value={head.receiving_party_id || ''} onChange={(e) => setHead({ ...head, receiving_party_id: Number(e.target.value) })}>
                                                <option value="">seçiniz…</option>
                                                {partyOptions(ctx.receiving_party_types).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </Input></div>
                                        <div className="flex-fill"><Label>Teslim alan kişi</Label>
                                            <Input value={head.receiving_person} placeholder={userName || 'Ad soyad'} onChange={(e) => setHead({ ...head, receiving_person: e.target.value })} /></div>
                                    </div>
                                    <div className="d-flex mt-2">
                                        <div className="flex-fill mr-2"><Label>{derived.transport_mode === 'GEMI' ? 'Gemi / sefer' : derived.transport_mode === 'TREN' ? 'Vagon no' : 'Plaka / araç'}</Label>
                                            <Input value={head.transport_ref} onChange={(e) => setHead({ ...head, transport_ref: e.target.value })} /></div>
                                        <div className="flex-fill"><Label>Belge no (B/L, CMR, CIM)</Label>
                                            <Input value={head.document_ref} onChange={(e) => setHead({ ...head, document_ref: e.target.value })} /></div>
                                    </div>
                                    {ctx.checks.length > 0 && (
                                        <div className="dt-flow">
                                            {ctx.checks.map((c) => (
                                                <div key={c.check} className={`dt-check ${c.ok ? '' : `fail-${c.mode}`}`}>
                                                    {c.ok ? '✓' : (c.mode === 'ENGEL' ? '✗' : '!')} <Badge color={c.ok ? 'success' : CHECK_COLOR[c.mode]}>{c.check}</Badge> {c.message}</div>
                                            ))}
                                            {warned && !blocked && (
                                                <label className="mt-1 mb-0"><input type="checkbox" checked={acceptWarn} onChange={(e) => setAcceptWarn(e.target.checked)} /> Uyarıları
                                                    gördüm, devam et (uyarılar tutanağa yazılır)</label>
                                            )}
                                        </div>
                                    )}
                                    {head.delivering_party_id > 0 && head.delivering_party_id === head.receiving_party_id && (
                                        <div className="dt-same">Teslim eden ve teslim alan aynı firma ({derived.delivering_party}): custody değişmiyor, devir-teslim
                                            kaydı gerekmez. Kapı kontrolü bu hareketi kendiliğinden geçirir. İsterseniz yine de kayıt açabilirsiniz.</div>
                                    )}
                                </>
                            )}
                            <Button block color="primary" className="mt-3" disabled={!headReady} onClick={() => setStep(2)}>
                                {head.delivering_party_id > 0 && head.delivering_party_id === head.receiving_party_id ? 'Yine de kontrol edip kaydet' : 'Kontrole başla'}</Button>
                            {point?.allow_two_step && (
                                <Button block color="light" className="mt-2" disabled={!headReady} onClick={() => setStep(5)}>
                                    Sadece teslim et (kontrol teslim alan tarafından sonra yapılacak)</Button>
                            )}
                            {point?.writes_delivery && !ctx.return_case && (
                                <Button block color="danger" outline className="mt-2" disabled={!head.to_location_id} onClick={startRefusal}>
                                    Teslim alınmadı / teslim edilemedi</Button>
                            )}
                        </>
                    )}
                </CardBody></Card>
            )}

            {step === 5 && (
                <Card><CardBody>
                    <div className="dt-hint">İki adımlı devir-teslim: teslim eden aracı bırakır ve imzalar. Teslim alan taraf aracı uygun koşullarda kontrol edip
                        imzaladığında kayıt tamamlanır. O zamana kadar devir-teslim açık istisna olarak izlenir (RUA: gece / mesai dışı teslimde
                        kontrol ertesi iş günü 12:00'ye kadar).</div>
                    <div className="dt-summary"><b>{vehicle.chassis_no}</b> · {point?.name_tr} · {H.location_name}<br />
                        {H.delivering_party} ({head.delivering_person}) → {H.receiving_party}</div>
                    <Label>Not</Label>
                    <Input value={startNote} placeholder="ör. Gece teslimi, araçlar 3. sıraya bırakıldı" onChange={(e) => setStartNote(e.target.value)} />
                    <div className="mt-2" />
                    <SignaturePad label="Teslim eden" name={`${head.delivering_person} (${H.delivering_party})`}
                        onChange={(v) => setSig((s) => ({ ...s, delivering: v }))} />
                    <div className="d-flex mt-3">
                        <Button color="secondary" className="mr-2" onClick={() => setStep(1)}>Geri</Button>
                        <Button color="primary" className="flex-fill" disabled={saving || !sig.delivering} onClick={submitStart}>
                            {saving ? 'Kaydediliyor…' : 'Teslimi kaydet'}</Button>
                    </div>
                </CardBody></Card>
            )}

            {step === 7 && (
                <Card><CardBody>
                    <div className="dt-hint">Araç teslim edilemedi: neden kaydedilir, araç taşıyıcıyla depoya döner. Depo girişinde (bayi iadesi okutması)
                        resepsiyon alanına alınır; Control Tower nedeni inceleyip yeni sevk, stok veya blokaj kararı verir.</div>
                    <div className="dt-summary"><b>{vehicle.chassis_no}</b> · {H.location_name || '—'}<br />
                        {H.delivering_party} ({head.delivering_person}){ctx?.destination_name ? ` · emirdeki varış: ${ctx.destination_name}` : ''}</div>
                    <Label>Neden *</Label>
                    <Input type="select" value={refusal.reason_code} onChange={(e) => setRefusal({ ...refusal, reason_code: e.target.value })}>
                        <option value="">— seçiniz —</option>
                        {(md?.refusal_reasons || []).filter((r) => r.is_active).map((r) => <option key={r.code} value={r.code}>{r.name_tr}</option>)}
                    </Input>
                    <Label className="mt-2">Açıklama{refusal.reason_code === 'DIGER' ? ' *' : ''}</Label>
                    <Input value={refusal.note} placeholder="ör. Araç GÜLAN'a ait, MEPA kabul etmedi" onChange={(e) => setRefusal({ ...refusal, note: e.target.value })} />
                    <Label className="mt-2">Bayi yetkilisi</Label>
                    <Input value={refusal.receiver_person} placeholder="Ad soyad / unvan" onChange={(e) => setRefusal({ ...refusal, receiver_person: e.target.value })} />
                    <Label className="mt-2">Fotoğraf (isteğe bağlı)</Label>
                    <Input type="file" accept="image/*" capture="environment" multiple
                        onChange={(e) => setRefusal({ ...refusal, photos: [...refusal.photos, ...Array.from(e.target.files)] })} />
                    {refusal.photos.map((f, i) => <div key={i} className="dt-muted">📷 {f.name}</div>)}
                    <div className="mt-2" />
                    <SignaturePad label="Şoför" name={`${head.delivering_person} (${H.delivering_party})`}
                        onChange={(v) => setSig((s) => ({ ...s, delivering: v }))} />
                    <SignaturePad label="Bayi (imzalamazsa boş bırakılır)" name={`${refusal.receiver_person || '—'} (${H.receiving_party})`}
                        onChange={(v) => setSig((s) => ({ ...s, receiving: v }))} />
                    <div className="d-flex mt-3">
                        <Button color="secondary" className="mr-2" onClick={() => setStep(1)}>Geri</Button>
                        <Button color="danger" className="flex-fill" onClick={submitRefusal}
                            disabled={saving || !refusal.reason_code || !sig.delivering || !head.delivering_person.trim()
                                || (refusal.reason_code === 'DIGER' && !refusal.note.trim())}>
                            {saving ? 'Kaydediliyor…' : 'Teslim edilemedi olarak kaydet'}</Button>
                    </div>
                </CardBody></Card>
            )}

            {step === 8 && refusalDone && (
                <Card><CardBody className="text-center">
                    <div className="dt-ok" style={{ color: '#d9822b' }}>↩</div>
                    <h5>Teslim edilemedi kaydedildi</h5>
                    <div className="dt-vin">{refusalDone.case_no}</div>
                    <div>{vehicle.chassis_no} · {(md?.refusal_reasons || []).find((r) => r.code === refusal.reason_code)?.name_tr}</div>
                    <div className="dt-muted mt-1">Araç depoya dönünce bayi iadesi okutmasıyla bu iade kaydına bağlanır.</div>
                    <Button color="secondary" block className="mt-3" onClick={restart}>Yeni işlem</Button>
                </CardBody></Card>
            )}

            {step === 6 && startDone && (
                <Card><CardBody className="text-center">
                    <div className="dt-ok">✓</div>
                    <h5>Teslim kaydedildi, kontrol bekleniyor</h5>
                    <div className="dt-vin">{startDone.start_no}</div>
                    <div>{startDone.chassis_no} · {H.delivering_party} → {H.receiving_party}</div>
                    <div className="dt-muted mt-1">Teslim alan taraf aracı kontrol edip imzalayana kadar bu devir-teslim açık listede görünür.</div>
                    <Button color="secondary" block className="mt-3" onClick={restart}>Yeni işlem</Button>
                </CardBody></Card>
            )}

            {step === 2 && (
                <Card><CardBody>
                    <div className="dt-hint">RUA: 16 kontrol noktasında, araç taşıma durumunda (yıkanmamış, korumalı) ve yeterli ışıkta kontrol ediniz. Süre araç başına en fazla 3 dakika.
                        {point && ` ${point.name_tr}: teslim eden ${H.delivering_party}, teslim alan ${H.receiving_party}.`}</div>

                    {lines.map((l, i) => (
                        <div key={i} className={`dt-line ${l.line_type.toLowerCase()}`}>
                            <div className="d-flex justify-content-between">
                                <b>{i + 1}. {l.line_type === 'HASAR' ? 'Hasar' : l.line_type === 'CEKINCE' ? 'Çekince' : 'Gözlem'}</b>
                                <Button size="sm" color="link" className="p-0 text-danger" onClick={() => setLines(lines.filter((_, k) => k !== i))}>Sil</Button>
                            </div>
                            {l.line_type === 'HASAR' ? (
                                <div>{zones[l.zone_code]?.name_tr} · <b>{l.component_code}</b> {comps[l.component_code]?.name_tr} · <b>{l.damage_code}</b> {dmg[l.damage_code]?.name_tr}
                                    {l.size_text && ` · ${l.size_text}`}{l.note && <div className="dt-muted">{l.note}</div>}
                                    <div className="dt-thumbs">{l.photos.map((f, k) => <img key={k} src={URL.createObjectURL(f)} alt="" />)}</div></div>
                            ) : (
                                <div>{obs[l.observation_code]?.name_tr}
                                    <Input bsSize="sm" className="mt-1" placeholder="Açıklama (isteğe bağlı)" value={l.note}
                                        onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, note: e.target.value } : x)))} /></div>
                            )}
                        </div>
                    ))}

                    {draft ? (
                        <div className="dt-draft">
                            <Label>Bölge</Label>
                            <div className="dt-chips">
                                {md.zones.map((z) => (
                                    <button key={z.code} type="button" disabled={z.is_excluded} title={z.is_excluded ? 'Kapsam dışı: gözlem olarak giriniz' : ''}
                                        className={`dt-chip ${draft.zone_code === z.code ? 'on' : ''}`} onClick={() => setDraft({ ...draft, zone_code: z.code })}>{z.name_tr}</button>
                                ))}
                            </div>
                            <Label className="mt-2">Parça (RUA kod no)</Label>
                            <Input bsSize="sm" placeholder="Kod veya ad ile ara: 56, kapı, tampon…" value={componentFilter}
                                onChange={(e) => setComponentFilter(e.target.value)} />
                            <Input type="select" size={5} className="dt-complist" value={draft.component_code}
                                onChange={(e) => setDraft({ ...draft, component_code: e.target.value,
                                    damage_code: ['33', '53', '74', '94'].includes(e.target.value) ? 'CE' : (draft.damage_code === 'CE' ? '' : draft.damage_code) })}>
                                {filteredComps.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name_tr}</option>)}
                            </Input>
                            <Label className="mt-2">Hasar kodu</Label>
                            <div className="dt-codes">
                                {md.damage_codes.map((d) => {
                                    const tyre = ['33', '53', '74', '94'].includes(draft.component_code);
                                    const off = draft.component_code && (tyre !== (d.code === 'CE'));
                                    return (
                                        <button key={d.code} type="button" disabled={off}
                                            className={`dt-code ${draft.damage_code === d.code ? 'on' : ''}`} onClick={() => setDraft({ ...draft, damage_code: d.code })}>
                                            <b>{d.code}</b><span>{d.name_tr}</span></button>
                                    );
                                })}
                            </div>
                            {draft.damage_code && <div className="dt-threshold"><b>RUA ölçütü:</b> {dmg[draft.damage_code].threshold}</div>}
                            <div className="d-flex mt-2">
                                <Input bsSize="sm" className="mr-2" placeholder="Boyut (ör. L 70 mm)" value={draft.size_text}
                                    onChange={(e) => setDraft({ ...draft, size_text: e.target.value })} />
                            </div>
                            <Input bsSize="sm" className="mt-2" placeholder="Açıklama (konum, ayrıntı)" value={draft.note}
                                onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
                            <Label className="mt-2">Fotoğraflar ({draft.photos.length})</Label>
                            <Input type="file" accept="image/*" capture="environment" multiple
                                onChange={(e) => setDraft({ ...draft, photos: [...draft.photos, ...Array.from(e.target.files)] })} />
                            <div className="dt-thumbs">{draft.photos.map((f, k) => <img key={k} src={URL.createObjectURL(f)} alt="" />)}</div>
                            <div className="d-flex mt-2">
                                <Button color="secondary" className="flex-fill mr-2" onClick={() => setDraft(null)}>Vazgeç</Button>
                                <Button color="danger" className="flex-fill" onClick={addDamage}>Hasarı ekle</Button>
                            </div>
                        </div>
                    ) : obsOpen ? (
                        <div className="dt-draft">
                            <Label>Gözlem / çekince</Label>
                            {md.observations.map((o) => (
                                <button key={o.code} type="button" className="dt-obs" onClick={() => addObservation(o.code)}>
                                    <Badge color={o.line_type === 'CEKINCE' ? 'warning' : 'info'}>{o.line_type === 'CEKINCE' ? 'Çekince' : 'Gözlem'}</Badge> {o.name_tr}</button>
                            ))}
                            <Button size="sm" color="secondary" className="mt-2" onClick={() => setObsOpen(false)}>Kapat</Button>
                        </div>
                    ) : (
                        <div className="d-flex mt-2">
                            <Button color="danger" className="flex-fill mr-2" onClick={() => setDraft({ ...emptyDamage })}>+ Hasar</Button>
                            <Button color="info" className="flex-fill" onClick={() => setObsOpen(true)}>+ Gözlem / çekince</Button>
                        </div>
                    )}

                    {!draft && !obsOpen && (
                        <div className="d-flex mt-3">
                            <Button color="secondary" className="mr-2" onClick={() => setStep(1)}>Geri</Button>
                            <Button color={damageCount ? 'danger' : 'success'} className="flex-fill" onClick={() => setStep(3)}>
                                {damageCount ? `İmzaya geç (${damageCount} hasar)` : 'Hasarsız, imzaya geç'}</Button>
                        </div>
                    )}
                </CardBody></Card>
            )}

            {step === 3 && (
                <Card><CardBody>
                    <div className="dt-summary">
                        <div><b>{vehicle.chassis_no}</b> · {point?.name_tr} · {H.location_name}</div>
                        <div>Sonuç: <Badge color={damageCount ? 'danger' : 'success'}>{damageCount ? `HASARLI (${damageCount})` : 'HASARSIZ'}</Badge>
                            {' '}Gözlem/çekince: {lines.length - damageCount}</div>
                    </div>
                    {startRec ? (
                        <div className="dt-sign">
                            <div className="dt-sign-head"><span><b>Teslim eden</b>: {head.delivering_person} ({H.delivering_party})</span></div>
                            <div className="dt-stored">
                                <StoredSignature path={startRec.delivering_signature} />
                                <div className="dt-muted">Teslimde imzalandı: {startRec.start_no}, {moment(startRec.started_at).format('DD.MM.YYYY HH:mm')}</div>
                            </div>
                        </div>
                    ) : (
                        <SignaturePad label="Teslim eden" name={`${head.delivering_person} (${H.delivering_party})`}
                            onChange={(v) => setSig((s) => ({ ...s, delivering: v }))} />
                    )}
                    <SignaturePad label="Teslim alan" name={`${head.receiving_person || userName} (${H.receiving_party})`}
                        onChange={(v) => setSig((s) => ({ ...s, receiving: v }))} />
                    {point && point.pod_document !== 'KAPALI' && (
                        <div className="dt-pod mt-2">
                            <Label className="mb-1">İmzalı teslim belgesi (irsaliye / CMR){point.pod_document === 'ENGEL' ? ' *' : ''}</Label>
                            <Input type="file" accept="image/*,application/pdf" capture="environment" multiple
                                onChange={(e) => setPodFiles([...podFiles, ...Array.from(e.target.files)])} />
                            {podFiles.map((f, i) => (
                                <div key={i} className="dt-muted">📄 {f.name} <a href="#" onClick={(e) => { e.preventDefault(); setPodFiles(podFiles.filter((_, j) => j !== i)); }}>kaldır</a></div>
                            ))}
                            <div className="dt-muted">Teslim belgesinin fotoğrafı tutanağa ve teslimat kaydına bağlanır (POD).</div>
                        </div>
                    )}
                    <div className="dt-muted">İmzalanan tutanak değiştirilemez ve silinemez. Düzeltme gerekirse yeni devir-teslim kaydı açılır.</div>
                    <div className="d-flex mt-3">
                        <Button color="secondary" className="mr-2" onClick={() => setStep(2)}>Geri</Button>
                        <Button color="primary" className="flex-fill" disabled={saving || (!startRec && !sig.delivering) || !sig.receiving
                            || (point?.pod_document === 'ENGEL' && podFiles.length === 0)} onClick={submit}>
                            {saving ? 'Kaydediliyor…' : 'Tutanağı onayla'}</Button>
                    </div>
                </CardBody></Card>
            )}

            {step === 4 && done && (
                <Card><CardBody className="text-center">
                    <div className="dt-ok">✓</div>
                    <h5>{done.pending ? 'Kayıt bekletiliyor' : 'Tutanak kaydedildi'}</h5>
                    <div className="dt-vin">{done.handover_no}</div>
                    {done.pending && <div className="text-warning">Araç tanımlı değil; araç tanımlandığında kayıt numarası verilip zincire bağlanacak.</div>}
                    <div>{vehicle.chassis_no} · {done.result === 'HASARLI' ? 'HASARLI' : 'HASARSIZ'}</div>
                    {point?.writes_delivery && <div className="mt-1"><Badge color="info">Teslimat kaydedildi (POD)</Badge></div>}
                    {!done.pending && <div className="dt-hash">Kayıt özeti: {done.record_hash.slice(0, 16)}…<br />Önceki kayıt: {done.prev_hash.slice(0, 16)}…</div>}
                    {!done.pending && <Button color="primary" block className="mt-3" onClick={() => downloadTutanakPdf(done.id).catch(() => toast.error('PDF oluşturulamadı'))}>Tutanak PDF</Button>}
                    <Button color="secondary" block onClick={restart}>Yeni kontrol</Button>
                </CardBody></Card>
            )}
        </div>
    );
};

export default DevirTeslim;
